import clsx from 'clsx';
import i18next from 'i18next';
import React, { Suspense, useCallback, useEffect, useRef } from 'react';
import { useSelector, useStore } from 'react-redux';
// @ts-expect-error
import Filmstrip from '../../../../../modules/UI/videolayout/Filmstrip';
import { getCurrentConference } from '../../../base/conference/functions';
import { translate } from '../../../base/i18n/functions.web';
import { getLocalParticipant } from '../../../base/participants/functions';
import { getVerticalViewMaxWidth } from '../../../filmstrip/functions.web';
import { getToolboxHeight } from '../../../toolbox/functions.web';
import { isLayoutTileView } from '../../../video-layout/functions.any';
import { WHITEBOARD_UI_OPTIONS } from '../../constants';
import { getCollabDetails, getCollabServerUrl, getStorageBackendUrl, isWhiteboardOpen, isWhiteboardVisible } from '../../functions';
const LazyExcalidrawApp = React.lazy(async () => {
    const [{ ExcalidrawApp }] = await Promise.all([
        import(/* webpackChunkName: "excalidraw" */ '@jitsi/excalidraw'),
        import(/* webpackChunkName: "excalidraw" */ '@jitsi/excalidraw/index.css')
    ]);
    return { default: ExcalidrawApp };
});
/**
 * Space taken by meeting elements like the subject and the watermark.
 */
const HEIGHT_OFFSET = 80;
/**
 * The Whiteboard component.
 *
 * @param {Props} props - The React props passed to this component.
 * @returns {JSX.Element} - The React component.
 */
const Whiteboard = (props) => {
    const excalidrawAPIRef = useRef(null);
    const collabAPIRef = useRef(null);
    const isOpen = useSelector(isWhiteboardOpen);
    const isVisible = useSelector(isWhiteboardVisible);
    const isInTileView = useSelector(isLayoutTileView);
    const { clientHeight, videoSpaceWidth } = useSelector((state) => state['features/base/responsive-ui']);
    const { visible: filmstripVisible, isResizing: isFilmstripResizing } = useSelector((state) => state['features/filmstrip']);
    const isChatResizing = useSelector((state) => state['features/chat'].isResizing);
    const isResizing = isFilmstripResizing || isChatResizing;
    const filmstripWidth = useSelector(getVerticalViewMaxWidth);
    const collabDetails = useSelector(getCollabDetails);
    const collabServerUrl = useSelector(getCollabServerUrl);
    const storageBackendUrl = useSelector(getStorageBackendUrl);
    const { defaultRemoteDisplayName } = useSelector((state) => state['features/base/config']);
    const localParticipantName = useSelector(getLocalParticipant)?.name || defaultRemoteDisplayName || 'Fellow Jitster';
    const jwt = useSelector((state) => state['features/base/jwt']).jwt || '';
    const store = useStore();
    const state = store.getState();
    const conference = getCurrentConference(state);
    const sessionId = conference?.getMeetingUniqueId();
    const roomJid = conference?.room?.roomjid;
    const meetingDetails = {
        sessionId: sessionId ?? '',
        roomJid: roomJid ?? '',
        jwt: jwt
    };
    useEffect(() => {
        if (!collabAPIRef.current) {
            return;
        }
        collabAPIRef.current.setUsername(localParticipantName);
    }, [localParticipantName]);
    /**
    * Computes the width and the height of the component.
    *
    * @returns {IDimensions} - The dimensions of the component.
    */
    const getDimensions = () => {
        let width;
        let height;
        if (interfaceConfig.VERTICAL_FILMSTRIP) {
            if (filmstripVisible) {
                width = videoSpaceWidth - filmstripWidth;
            }
            else {
                width = videoSpaceWidth;
            }
            height = clientHeight - getToolboxHeight();
        }
        else {
            if (filmstripVisible) {
                height = clientHeight - Filmstrip.getFilmstripHeight();
            }
            else {
                height = clientHeight;
            }
            width = videoSpaceWidth;
        }
        return {
            width: `${width}px`,
            height: `${height - HEIGHT_OFFSET}px`
        };
    };
    const getExcalidrawAPI = useCallback(excalidrawAPI => {
        if (excalidrawAPIRef.current) {
            return;
        }
        excalidrawAPIRef.current = excalidrawAPI;
    }, []);
    const getCollabAPI = useCallback(collabAPI => {
        if (collabAPIRef.current) {
            return;
        }
        collabAPIRef.current = collabAPI;
        collabAPIRef.current.setUsername(localParticipantName);
    }, [localParticipantName]);
    return (React.createElement("div", { className: clsx(isResizing && 'disable-pointer', 'whiteboard-container'), style: {
            ...getDimensions(),
            marginTop: `${HEIGHT_OFFSET}px`,
            display: `${isInTileView || !isVisible ? 'none' : 'block'}`
        } }, isOpen && (React.createElement("div", { className: 'excalidraw-wrapper' }, /*
          * Excalidraw renders a few lvl 2 headings. This is
          * quite fortunate, because we actually use lvl 1
          * headings to mark the big sections of our app. So make
          * sure to mark the Excalidraw context with a lvl 1
          * heading before showing the whiteboard.
          */
        React.createElement("span", { "aria-level": 1, className: 'sr-only', role: 'heading' }, props.t('whiteboard.accessibilityLabel.heading')),
        React.createElement(Suspense, { fallback: null },
            React.createElement(LazyExcalidrawApp, { collabDetails: collabDetails, collabServerUrl: collabServerUrl, excalidraw: {
                    isCollaborating: true,
                    langCode: i18next.language,
                    theme: 'light',
                    UIOptions: WHITEBOARD_UI_OPTIONS
                }, getCollabAPI: getCollabAPI, getExcalidrawAPI: getExcalidrawAPI, meetingDetails: meetingDetails, storageBackendUrl: storageBackendUrl }))))));
};
export default translate(Whiteboard);
